import { BoxPlotDatum } from '@nivo/boxplot';
import { ScaleSpec } from '@nivo/scales';
import {
  ScatterPlotCommonProps,
  ScatterPlotDatum,
  ScatterPlotRawSerie,
} from '@nivo/scatterplot';
import { first, last, mean, sortBy } from 'lodash';
import moment, { duration } from 'moment';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { Enum } from '~/data/enums';
import { CompensationEqualityReportEntry } from '~/scenes/PortalModules/Analytics/Rewarding/CompensationEqualityReport/data';
import { mathRoundToDecimals } from '~/utils/helper';

export enum ScatterXAxis {
  Age = 'age',
  TotalEmploymentTime = 'totalEmploymentTime',
  PositionEmploymentTime = 'positionEmploymentTime',
}

type GenderKPIs = {
  employeeCount: number;
  averageAge: number;
  averageSalary: number;
  averageCompensation: number;
};

type PercentageKPI = {
  percentage: number;
  isValid: boolean;
};

export type ScatterTooltipValueConfig = {
  label: string;
  valueQualifier?: string;
};

export type ScatterXConfig = {
  code: ScatterXAxis;
  name: string;
  tooltip: ScatterTooltipValueConfig;
};

type PropsType = {
  isLoading: boolean;
  rows: CompensationEqualityReportEntry[];
};

type UseCompensationEqualityChartsOutput = {
  kpi: {
    female: GenderKPIs;
    male: GenderKPIs;
    femalesPercentageOfAll: PercentageKPI;
    femalesPercentageToMales: PercentageKPI;
  };
  charts: {
    scatterPlot: {
      data: ScatterPlotRawSerie<ScatterPlotDatum>[];
      xScale: ScatterPlotCommonProps<ScatterPlotDatum>['xScale'];
      yScale: ScatterPlotCommonProps<ScatterPlotDatum>['yScale'];
    };
    boxPlot: {
      data: BoxPlotDatum[];
      minValue: number | 'auto';
      maxValue: number | 'auto';
    };
  };
  scatterXSelector: {
    value: ScatterXAxis;
    setValue: Dispatch<SetStateAction<ScatterXAxis>>;
    options: {
      value: string;
      label: string;
    }[];
    config: ScatterXConfig[];
    currentConfig: ScatterXConfig;
  };
};

export const useCompensationEqualityCharts = (props: PropsType) => {
  const { rows } = props;

  const { t } = useTranslation();

  const [scatterXData, setScatterXData] = useState<ScatterXAxis>(
    ScatterXAxis.TotalEmploymentTime,
  );

  const { scatterXConfig, scatterXOptions, currentScatterXConfig } =
    useMemo(() => {
      const scatterXConfig: ScatterXConfig[] = [
        {
          code: ScatterXAxis.TotalEmploymentTime,
          name: `${t('totalEmploymentTime')} (${t('laYears')})`,
          tooltip: {
            label: t('totalEmploymentTime'),
            valueQualifier: t('laYears'),
          },
        },
        {
          code: ScatterXAxis.PositionEmploymentTime,
          name: `${t('positionEmploymentTime')} (${t('laYears')})`,
          tooltip: {
            label: t('positionEmploymentTime'),
            valueQualifier: t('laYears'),
          },
        },
        {
          code: ScatterXAxis.Age,
          name: t('laAge').trim(),
          tooltip: {
            label: t('laAge'),
            valueQualifier: t('laYears'),
          },
        },
      ];

      const scatterXOptions: Enum[] = scatterXConfig.map(
        ({ tooltip: _tooltip, ...rest }) => rest,
      );

      const currentScatterXConfig = scatterXConfig.find(
        ({ code }) => code === scatterXData,
      );

      return {
        scatterXOptions,
        scatterXConfig,
        currentScatterXConfig,
      };
    }, [t, scatterXData]);

  const onChangeScatterXData = useCallback(
    (value: string) => {
      const options: string[] = Object.values(ScatterXAxis).map((v) =>
        v.toString(),
      );

      if (options.includes(value)) {
        setScatterXData(value as ScatterXAxis);
      }
    },
    [setScatterXData],
  );

  const { kpi, maleRows, femaleRows } = useMemo((): {
    kpi: UseCompensationEqualityChartsOutput['kpi'];
    femaleRows: CompensationEqualityReportEntry[];
    maleRows: CompensationEqualityReportEntry[];
  } => {
    const femaleRows = rows.filter(({ fEmpGender }) => fEmpGender === 'F');
    const maleRows = rows.filter(({ fEmpGender }) => fEmpGender === 'M');

    const getGenderKPIs = (
      rows: CompensationEqualityReportEntry[],
    ): GenderKPIs => {
      const hasRows = rows.length > 0;

      const ages = rows.map(({ fEmpDateOfBirth }) =>
        duration(moment().diff(moment(fEmpDateOfBirth, 'YYYY-MM-DD'))),
      );
      const averageAge: number = hasRows ? duration(mean(ages)).years() : 0;

      const salaries = rows.map((row) => row.aggregated.salary ?? 0);
      const averageSalary: number = hasRows
        ? mathRoundToDecimals(mean(salaries), 2)
        : 0;

      const compensations = rows.map((row) => row.aggregated.total ?? 0);
      const averageCompensation: number = hasRows
        ? mathRoundToDecimals(mean(compensations), 2)
        : 0;

      return {
        employeeCount: rows.length,
        averageAge,
        averageSalary,
        averageCompensation,
      };
    };

    const getPercentageKPI = (
      count: number,
      ofCount: number,
    ): PercentageKPI => {
      return {
        percentage:
          count > 0 && ofCount > 0
            ? mathRoundToDecimals((count / ofCount) * 100, 1)
            : 0,
        isValid: ofCount > 0,
      };
    };

    return {
      kpi: {
        female: getGenderKPIs(femaleRows),
        male: getGenderKPIs(maleRows),
        femalesPercentageOfAll: getPercentageKPI(
          femaleRows.length,
          rows.length,
        ),
        femalesPercentageToMales: getPercentageKPI(
          femaleRows.length,
          maleRows.length,
        ),
      },
      femaleRows,
      maleRows,
    };
  }, [rows]);

  const charts = useMemo((): UseCompensationEqualityChartsOutput['charts'] => {
    const getScatterXData = (row: CompensationEqualityReportEntry) => {
      const { fEmpDateOfBirth, fEmpTotalDays, fEmpTotalPosDays } = row;

      switch (scatterXData) {
        case ScatterXAxis.Age:
          return duration(
            moment().diff(moment(fEmpDateOfBirth, 'YYYY-MM-DD')),
          ).years();
        case ScatterXAxis.TotalEmploymentTime:
          return duration(fEmpTotalDays, 'days').years();
        case ScatterXAxis.PositionEmploymentTime:
          return duration(fEmpTotalPosDays, 'days').years();
        default:
          return 0;
      }
    };

    const getScatterGroupData = (
      id: string,
      rows: CompensationEqualityReportEntry[],
    ): ScatterPlotRawSerie<ScatterPlotDatum> => {
      return {
        id,
        data: rows.map((row) => {
          return {
            x: getScatterXData(row),
            y: row.aggregated.total,
          };
        }),
      };
    };

    const scatterPlotData = [
      getScatterGroupData(t('laFemale'), femaleRows),
      getScatterGroupData(t('laMale'), maleRows),
    ];

    const scatterXValues: number[] = sortBy(
      scatterPlotData
        .map(({ data }) => data.map(({ x }) => x))
        .flat()
        .reduce((acc, x) => {
          if (typeof x === 'number') {
            acc.push(x);
          }
          return acc;
        }, [] as number[]),
    );

    const scatterYValues: number[] = sortBy(
      scatterPlotData
        .map(({ data }) => data.map(({ y }) => y))
        .flat()
        .reduce((acc, y) => {
          if (typeof y === 'number') {
            acc.push(y);
          }
          return acc;
        }, [] as number[]),
    );

    const getMinValue = (values: number[]): number | 'auto' => {
      const smallest = first(values);

      if (!!smallest) {
        return smallest > 1 ? smallest - 1 : smallest;
      }

      return 'auto';
    };

    const getMaxValue = (values: number[]): number | 'auto' => {
      const largest = last(values);

      if (!!largest) {
        return largest + 1;
      }

      return 'auto';
    };

    const xScale: ScaleSpec = {
      type: 'linear',
      min: getMinValue(scatterXValues),
      max: getMaxValue(scatterXValues),
    };

    const yScale: ScaleSpec = {
      type: 'linear',
      min: getMinValue(scatterYValues),
      max: getMaxValue(scatterYValues),
    };

    const getBoxPlotData = (
      group: string,
      rows: CompensationEqualityReportEntry[],
    ): BoxPlotDatum[] => {
      return rows.map((row): BoxPlotDatum => {
        const { fEmpGender, aggregated } = row;

        return {
          group,
          subgroup: fEmpGender,
          value: aggregated.total,
        };
      });
    };

    const boxPlotData = [
      sortBy(getBoxPlotData(t('laFemale'), femaleRows), 'value'),
      sortBy(getBoxPlotData(t('laMale'), maleRows), 'value'),
    ].flat();

    const boxPlotValues: number[] = sortBy(boxPlotData, 'value').map(
      (dataPoint) => dataPoint.value as number,
    );
    const boxPlotMinMax = {
      minValue: getMinValue(boxPlotValues),
      maxValue: getMaxValue(boxPlotValues),
    };

    return {
      scatterPlot: {
        data: scatterPlotData,
        xScale,
        yScale,
      },
      boxPlot: {
        data: boxPlotData,
        ...boxPlotMinMax,
      },
    };
  }, [femaleRows, maleRows, scatterXData, t]);

  return {
    kpi,
    charts,
    scatterXSelector: {
      value: scatterXData,
      onChange: onChangeScatterXData,
      options: scatterXOptions,
      config: scatterXConfig,
      currentConfig: currentScatterXConfig,
    },
  };
};
